import React from 'react';
import { Button } from '@/components/ui/button';
import { Scale, Shield, FileText, ArrowRight } from 'lucide-react';

const Landing = () => {
  const handleLogin = () => {
    // Redirect to root so session_id hash can be processed properly
    const redirectUrl = `${window.location.origin}/`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Scale className="w-8 h-8 text-blue-700" />
              <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>LegalCheck</span>
            </div>
            <div className="hidden sm:inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">
              FREE
            </div>
          </div>
          <Button 
            data-testid="login-button"
            onClick={handleLogin}
            className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-2 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Sign in with Google
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-16">
        <div className="text-center space-y-8">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
              AI-Powered Legal Analysis
            </div>
            <div className="inline-block px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-bold">
              🎉 100% FREE TO USE
            </div>
          </div>
          
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Do You Have Grounds
            <br />
            <span className="text-blue-700">For a Legal Case?</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Get instant AI-powered analysis of your legal situation <strong className="text-green-700">completely free</strong>. Our advanced system uses three leading AI models
            to evaluate your case and provide comprehensive insights with similar case precedents.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
            <Button 
              data-testid="get-started-button"
              onClick={handleLogin}
              size="lg"
              className="bg-blue-700 hover:bg-blue-800 text-white font-bold px-8 py-6 rounded-full text-lg shadow-2xl hover:shadow-blue-300 transition-all duration-300 transform hover:scale-105"
            >
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Disclaimer */}
          <div className="mt-12 p-6 bg-amber-50 border-2 border-amber-200 rounded-xl max-w-3xl mx-auto">
            <p className="text-sm text-amber-900 font-medium">
              <strong>⚠️ Important Disclaimer:</strong> This service provides informational analysis only and does not constitute legal advice. 
              Always consult with a licensed attorney before taking any legal action.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-slate-100">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-6">
              <Scale className="w-7 h-7 text-blue-700" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Triple AI Analysis
            </h3>
            <p className="text-slate-600 leading-relaxed">
              Your case is analyzed by OpenAI GPT-5, Claude Sonnet 4, and Gemini 2.5 Pro for comprehensive evaluation.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-slate-100">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <FileText className="w-7 h-7 text-green-700" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Similar Case Precedents
            </h3>
            <p className="text-slate-600 leading-relaxed">
              Review anonymized similar cases with outcomes to understand potential success likelihood.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-slate-100">
            <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mb-6">
              <Shield className="w-7 h-7 text-purple-700" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Instant Results
            </h3>
            <p className="text-slate-600 leading-relaxed">
              Get your comprehensive legal case analysis within minutes, not days or weeks.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 
          className="text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-12"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          How It Works
        </h2>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
              1
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Submit Your Case</h3>
            <p className="text-slate-600">Describe your legal situation in detail</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
              2
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">AI Analysis</h3>
            <p className="text-slate-600">Three AI models evaluate your case</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
              3
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Get Results</h3>
            <p className="text-slate-600">Review verdict and similar cases</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Scale className="w-6 h-6 text-blue-700" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>LegalCheck</span>
            </div>
            <p className="text-slate-600 text-sm text-center">
              © 2025 LegalCheck. Not a substitute for professional legal advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;